import { logger } from '@/utils/logger';

/** Private mode throws on access: reads fall back to empty, writes fail quietly. */

const ANSWERS_KEY = 'leansl.answers';
const PROGRESS_KEY = 'leansl.progress';
const SESSION_KEY = 'leansl.session';
const DEVICE_KEY = 'leansl.device';

function readObject(key: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? { ...parsed } : {};
  } catch {
    return {};
  }
}

function mergeObject(key: string, patch: Record<string, unknown>): void {
  try {
    localStorage.setItem(key, JSON.stringify({ ...readObject(key), ...patch }));
  } catch (error) {
    logger.warn(`storage: could not persist ${key}`, error);
  }
}

// Questionnaire answers — persisted so a reload mid-flow keeps progress.

const ANSWER_KEYS = [
  'gender',
  'goal',
  'motivation',
  'birthday',
  'nationality',
  'heightCm',
  'weightKg',
  'targetWeightKg',
  'activityLevel',
  'dietFamiliarity',
  'dietaryPreference',
  'allergies',
  'meatPreference',
  'weekendChoices',
  'eatingHabits',
  'cookingSkill',
  'mealTiming',
] as const;

export type AnswerKey = (typeof ANSWER_KEYS)[number];

export type Answers = Partial<Record<AnswerKey, string>>;

export function getAnswer(key: AnswerKey): string | undefined {
  // Narrowed, not asserted: stored JSON came from a possibly older build.
  const value = readObject(ANSWERS_KEY)[key];
  return typeof value === 'string' ? value : undefined;
}

export function setAnswer(key: AnswerKey, value: string): void {
  mergeObject(ANSWERS_KEY, { [key]: value });
}

export function getAnswers(key: AnswerKey): string[] {
  const raw = getAnswer(key);
  return raw === undefined || raw === '' ? [] : raw.split(',');
}

export function setAnswers(key: AnswerKey, values: readonly string[]): void {
  setAnswer(key, values.join(','));
}

export function getAllAnswers(): Answers {
  const stored = readObject(ANSWERS_KEY);
  const answers: Answers = {};
  for (const key of ANSWER_KEYS) {
    const value = stored[key];
    if (typeof value === 'string') answers[key] = value;
  }
  return answers;
}

export function markQuestionsComplete(): void {
  mergeObject(PROGRESS_KEY, { questionsComplete: true });
}

export function hasCompletedQuestions(): boolean {
  return readObject(PROGRESS_KEY)['questionsComplete'] === true;
}

// Session. Step-up tokens are NOT stored: a half-finished sign-up must not survive a reload.

export interface StoredSession {
  userId: string;
  accessToken: string;
  refreshToken: string;
}

export function getSession(): StoredSession | undefined {
  const stored = readObject(SESSION_KEY);
  const userId = stored['userId'];
  const accessToken = stored['accessToken'];
  const refreshToken = stored['refreshToken'];

  if (typeof userId !== 'string' || userId === '') return undefined;
  if (typeof accessToken !== 'string' || accessToken === '') return undefined;
  if (typeof refreshToken !== 'string' || refreshToken === '') return undefined;

  return { userId, accessToken, refreshToken };
}

export function setSession(session: StoredSession): void {
  mergeObject(SESSION_KEY, { ...session });
}

export function setAccessToken(accessToken: string): void {
  mergeObject(SESSION_KEY, { accessToken });
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (error) {
    logger.warn('storage: could not clear the session', error);
  }
}

// Device — must stay the same across launches, or every login registers a new device.

export function getDeviceId(): string | undefined {
  const stored = readObject(DEVICE_KEY)['deviceId'];
  return typeof stored === 'string' && stored !== '' ? stored : undefined;
}

export function setDeviceId(deviceId: string): void {
  mergeObject(DEVICE_KEY, { deviceId });
}
